/**
 * Server-Netz, Stufe 1a: der Vertrag einer Server-Meldung. (17.10.2026, Plan scratchpad/server-netz-plan.md)
 *
 * EIN Besitzer für Kennungen, Felder, Grenzen, Prüfung und Ampel — Speicher, Takt, Route und später das Tor rechnen alle damit.
 * Nur S6, S7 und S8 gehören dazu (Martin, 17.10.2026). Heute meldet nur S8 sich selbst, lokal, ohne SSH.
 *
 * ⚠️ Eine Meldung besteht NUR aus Zahlen, ja/nein und einem kurzen Versionswort. Nie ein Dateiname, ein Benutzer, eine Logzeile,
 *    ein Befehl. Der Einhängepunkt einer Platte (`/`, `/boot`) ist die einzige Zeichenkette mit Pfadform — streng geprüft.
 * ⚠️ Die Kennung kommt NIE aus dem Rumpf der Meldung, sondern vom Tor (später: `command=` in authorized_keys). Steht eine
 *    Kennung im Rumpf, wird sie verworfen — sie darf weder fälschen noch überstimmen.
 * ⚠️ Fehlt eine Quelle, ist der Wert `null` und die Ampel sagt „unbekannt“ — nie ein erfundenes Grün.
 */

export const SERVER_KENNUNGEN = ["s6", "s7", "s8"] as const;
export type ServerKennung = (typeof SERVER_KENNUNGEN)[number];

/** Anzeigename je Server (fest). */
export const SERVER_NAMEN: Readonly<Record<ServerKennung, string>> = { s6: "S6", s7: "S7", s8: "S8" };

export function istServerKennung(x: unknown): x is ServerKennung {
  return typeof x === "string" && (SERVER_KENNUNGEN as readonly string[]).includes(x);
}

/** Grenzen der Meldung. */
export const GRENZEN = {
  plattenHoechstens: 8,
  punktLaenge: 64,
  versionLaenge: 40,
  /** Laufzeit seit Start: höchstens zehn Jahre in Sekunden. */
  /** Größe eines Rumpfes (JSON) und Tiefe seiner Verschachtelung. */
  rumpfBytes: 4096,
  rumpfTiefe: 6,
  laufzeitSMax: 10 * 366 * 86_400,
  lastMax: 100_000,
  updatesMax: 100_000,
  /** Alter der letzten Sicherung: höchstens zehn Jahre in Stunden. */
  sicherungStundenMax: 10 * 366 * 24,
  /** Die Zeit einer Meldung darf so weit von der Empfangszeit abweichen. */
  zeitAbweichungMs: 10 * 60_000,
} as const;

/** Ampel-Schwellen (Plan Abschnitt 4/5). */
export const SCHWELLEN = {
  keineMeldungMs: 30 * 60_000,
  platteProzentRot: 10,
  platteProzentGelb: 20,
  sicherungStundenRot: 26,
  speicherProzentGelb: 10,
} as const;

export interface PlattenStand {
  /** Einhängepunkt, z. B. „/“. */
  punkt: string;
  /** Frei in Prozent, 0–100 (eine Nachkommastelle). */
  freiProzent: number;
}

export interface Meldung {
  kennung: ServerKennung;
  /** Zeitpunkt der Messung, ms seit 1970. */
  zeit: number;
  /** Laufzeit seit Start in Sekunden; `null` = unbekannt. */
  laufzeitS: number | null;
  /** Last der letzten Minute. */
  last: number | null;
  platten: PlattenStand[];
  speicherFreiProzent: number | null;
  updatesOffen: number | null;
  updatesSicherheit: number | null;
  neustartNoetig: boolean | null;
  /** Alter der letzten Sicherung in Stunden; `null` = unbekannt (nie geraten). */
  sicherungAlterH: number | null;
  boteVersion: string;
}

/** Die erlaubten Felder des Rumpfes — alles andere (auch `kennung`) wird verworfen. */
const FELDER = [
  "zeit", "laufzeitS", "last", "platten", "speicherFreiProzent", "updatesOffen", "updatesSicherheit",
  "neustartNoetig", "sicherungAlterH", "boteVersion",
] as const;

const PUNKT_ZEICHEN = /^\/[A-Za-z0-9/._-]*$/;

/** Ein Einhängepunkt: nur sichere Zeichen, kein „..“-Teil, keine Doppel-Schrägstriche, nicht zu lang. Rein. */
export function istPlattenPunkt(x: unknown): x is string {
  return typeof x === "string" && x.length <= GRENZEN.punktLaenge && PUNKT_ZEICHEN.test(x) && !x.includes("//") &&
    !x.split("/").includes("..");
}
const VERSION_ZEICHEN = /^[0-9A-Za-z.+-]+$/;

/** Steuerzeichen (C0, DEL, C1) sowie Zeilen- und Absatztrenner. */
function hatSteuerzeichen(s: string): boolean {
  for (const zeichen of s) {
    const n = zeichen.codePointAt(0) ?? 0;
    if (n <= 0x1f || (n >= 0x7f && n <= 0x9f) || n === 0x2028 || n === 0x2029) return true;
  }
  return false;
}

export type PruefErgebnis = { ok: true; meldung: Meldung } | { ok: false; grund: string };

function zahl(x: unknown, max: number): number | null | undefined {
  if (x === null) return null;
  if (typeof x !== "number" || !Number.isFinite(x) || x < 0 || x > max) return undefined;
  return x;
}

function ganz(x: unknown, max: number): number | null | undefined {
  const z = zahl(x, max);
  return typeof z === "number" && !Number.isInteger(z) ? undefined : z;
}

/** Ohne Steuerzeichen und nicht tiefer als `rumpfTiefe` (zu tief → `false`, kein Rekursions-Absturz). */
function ohneSteuerzeichen(x: unknown, tiefe = 0): boolean {
  if (tiefe > GRENZEN.rumpfTiefe) return false;
  if (typeof x === "string") return !hatSteuerzeichen(x);
  if (Array.isArray(x)) return x.every((v) => ohneSteuerzeichen(v, tiefe + 1));
  if (x && typeof x === "object") return Object.entries(x).every(([k, v]) => ohneSteuerzeichen(k, tiefe + 1) && ohneSteuerzeichen(v, tiefe + 1));
  return true;
}

/** Größe des Rumpfes als JSON; zu tief, zyklisch oder nicht darstellbar → `Infinity`. */
function rumpfGroesse(x: unknown): number {
  try {
    return JSON.stringify(x)?.length ?? Infinity;
  } catch {
    return Infinity;
  }
}

const runde1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * Prüft den Rumpf einer Meldung. Rein. `kennung` kommt vom Tor (nicht aus dem Rumpf); `jetzt` ist die Empfangszeit.
 * Unbekannte Felder und eine Kennung im Rumpf werden verworfen (nicht übernommen); zu große, falsch geformte oder mit
 * Steuerzeichen versehene Werte lehnen die ganze Meldung ab. Der Grund ist ein festes Wort, nie ein Teil des Rumpfes.
 */
export function pruefeMeldung(roh: unknown, kennung: unknown, jetzt: number): PruefErgebnis {
  if (!istServerKennung(kennung)) return { ok: false, grund: "kennung" };
  if (!roh || typeof roh !== "object" || Array.isArray(roh)) return { ok: false, grund: "form" };
  if (rumpfGroesse(roh) > GRENZEN.rumpfBytes) return { ok: false, grund: "zu-gross" };
  if (!ohneSteuerzeichen(roh)) return { ok: false, grund: "steuerzeichen" };
  const r = roh as Record<string, unknown>;
  const e: Record<string, unknown> = {};
  for (const f of FELDER) if (Object.prototype.hasOwnProperty.call(r, f)) e[f] = r[f];

  const zeit = e.zeit;
  if (typeof zeit !== "number" || !Number.isFinite(zeit) || Math.abs(zeit - jetzt) > GRENZEN.zeitAbweichungMs) {
    return { ok: false, grund: "zeit" };
  }
  const laufzeitS = e.laufzeitS === undefined ? null : zahl(e.laufzeitS, GRENZEN.laufzeitSMax);
  if (laufzeitS === undefined) return { ok: false, grund: "laufzeit" };

  const last = e.last === undefined ? null : zahl(e.last, GRENZEN.lastMax);
  if (last === undefined) return { ok: false, grund: "last" };

  if (!Array.isArray(e.platten) || e.platten.length > GRENZEN.plattenHoechstens) return { ok: false, grund: "platten" };
  const platten: PlattenStand[] = [];
  for (const p of e.platten) {
    if (!p || typeof p !== "object" || Array.isArray(p)) return { ok: false, grund: "platten" };
    const q = p as Record<string, unknown>;
    const frei = zahl(q.freiProzent, 100);
    if (
      !istPlattenPunkt(q.punkt) ||
      typeof frei !== "number"
    ) {
      return { ok: false, grund: "platten" };
    }
    platten.push({ punkt: q.punkt, freiProzent: runde1(frei) });
  }

  const speicher = e.speicherFreiProzent === undefined ? null : zahl(e.speicherFreiProzent, 100);
  if (speicher === undefined) return { ok: false, grund: "speicher" };

  const offen = e.updatesOffen === undefined ? null : ganz(e.updatesOffen, GRENZEN.updatesMax);
  const sicher = e.updatesSicherheit === undefined ? null : ganz(e.updatesSicherheit, GRENZEN.updatesMax);
  if (offen === undefined || sicher === undefined || (offen !== null && sicher !== null && sicher > offen)) {
    return { ok: false, grund: "updates" };
  }

  const neustart = e.neustartNoetig === undefined ? null : e.neustartNoetig;
  if (neustart !== null && typeof neustart !== "boolean") return { ok: false, grund: "neustart" };

  const sicherung = e.sicherungAlterH === undefined ? null : zahl(e.sicherungAlterH, GRENZEN.sicherungStundenMax);
  if (sicherung === undefined) return { ok: false, grund: "sicherung" };

  const version = e.boteVersion;
  if (typeof version !== "string" || version.length < 1 || version.length > GRENZEN.versionLaenge || !VERSION_ZEICHEN.test(version)) {
    return { ok: false, grund: "version" };
  }

  return {
    ok: true,
    meldung: {
      kennung,
      zeit,
      laufzeitS: laufzeitS === null ? null : Math.floor(laufzeitS),
      last: last === null ? null : runde1(last),
      platten,
      speicherFreiProzent: speicher === null ? null : runde1(speicher),
      updatesOffen: offen,
      updatesSicherheit: sicher,
      neustartNoetig: neustart,
      sicherungAlterH: sicherung === null ? null : runde1(sicherung),
      boteVersion: version,
    },
  };
}

export type Ampel = "gruen" | "gelb" | "rot" | "unbekannt";

/** Feste Wörter, warum die Ampel so steht — nie Text vom Boten. */
export type AmpelGrund =
  | "keine-meldung"
  | "platte-knapp"
  | "sicherung-alt"
  | "neustart-noetig"
  | "sicherheits-updates"
  | "platte-wird-knapp"
  | "speicher-knapp"
  | "platte-unbekannt"
  | "sicherung-unbekannt"
  | "updates-unbekannt"
  | "neustart-unbekannt";

/** Diese Gründe machen die Ampel rot — und nur sie läuten die Glocke. */
export const ROT_GRUENDE: readonly AmpelGrund[] = ["keine-meldung", "platte-knapp", "sicherung-alt"];

export interface AmpelStand {
  ampel: Ampel;
  gruende: AmpelGrund[];
  /** Sekunden seit der letzten Meldung (`null` ohne Meldung). */
  zuletztVorS: number | null;
}

/**
 * Die Ampel. Rein.
 *   rot     keine Meldung seit 30 min · Platte < 10 % · Sicherung > 26 h
 *   gelb    Neustart nötig · offene Sicherheits-Updates · Platte < 20 % · Speicher < 10 % frei
 *   unbekannt  keine Meldung je (Quelle fehlt) — oder eine geprüfte Quelle liefert nichts und nichts ist rot/gelb
 *   gruen   nur, wenn ALLES gemessen und in Ordnung ist
 */
export function berechneAmpel(letzte: Meldung | null | undefined, jetzt: number): AmpelStand {
  if (!letzte) return { ampel: "unbekannt", gruende: [], zuletztVorS: null };
  const alterMs = Math.max(0, jetzt - letzte.zeit);
  const zuletztVorS = Math.floor(alterMs / 1000);
  if (alterMs > SCHWELLEN.keineMeldungMs) return { ampel: "rot", gruende: ["keine-meldung"], zuletztVorS };

  const rot: AmpelGrund[] = [];
  const gelb: AmpelGrund[] = [];
  const unbekannt: AmpelGrund[] = [];

  if (letzte.platten.length === 0) unbekannt.push("platte-unbekannt");
  else {
    const knappste = Math.min(...letzte.platten.map((p) => p.freiProzent));
    if (knappste < SCHWELLEN.platteProzentRot) rot.push("platte-knapp");
    else if (knappste < SCHWELLEN.platteProzentGelb) gelb.push("platte-wird-knapp");
  }
  if (letzte.sicherungAlterH === null) unbekannt.push("sicherung-unbekannt");
  else if (letzte.sicherungAlterH > SCHWELLEN.sicherungStundenRot) rot.push("sicherung-alt");

  if (letzte.neustartNoetig === null) unbekannt.push("neustart-unbekannt");
  else if (letzte.neustartNoetig) gelb.push("neustart-noetig");

  if (letzte.updatesOffen === null) unbekannt.push("updates-unbekannt");
  if ((letzte.updatesSicherheit ?? 0) > 0) gelb.push("sicherheits-updates");
  if (letzte.speicherFreiProzent !== null && letzte.speicherFreiProzent < SCHWELLEN.speicherProzentGelb) gelb.push("speicher-knapp");

  if (rot.length) return { ampel: "rot", gruende: [...rot, ...gelb, ...unbekannt], zuletztVorS };
  if (gelb.length) return { ampel: "gelb", gruende: [...gelb, ...unbekannt], zuletztVorS };
  if (unbekannt.length) return { ampel: "unbekannt", gruende: unbekannt, zuletztVorS };
  return { ampel: "gruen", gruende: [], zuletztVorS };
}

/** Der Kontoschalter in den Einstellungen (nur Konto 1, Vorgabe AUS). */
export const SERVER_NETZ_FELD = "serverNetz";
/** Der Notschalter in der Umgebung des Dienstes: nur genau `1` lässt das Server-Netz laufen. */
export const SERVER_NETZ_NOTSCHALTER = "SERVER_NETZ";

/**
 * Läuft das Server-Netz? Beides muss stimmen: Notschalter genau `1` UND Kontoschalter ausdrücklich `true`. Rein.
 * `einstellungen` ist das geparste `users.settings` von Konto 1 (oder `null`).
 */
export function serverNetzAn(notschalter: string | undefined, einstellungen: unknown): boolean {
  if (notschalter !== "1") return false;
  if (!einstellungen || typeof einstellungen !== "object") return false;
  return (einstellungen as Record<string, unknown>)[SERVER_NETZ_FELD] === true;
}

/** Glocken-Titel/-Satz je Grund: feste Wörter, ohne Umlaut-Fallen. */
export const GRUND_WORTE: Readonly<Record<AmpelGrund, string>> = {
  "keine-meldung": "keine Meldung seit über 30 Minuten",
  "platte-knapp": "Platte fast voll (unter 10 % frei)",
  "sicherung-alt": "letzte Sicherung älter als 26 Stunden",
  "neustart-noetig": "Neustart nötig",
  "sicherheits-updates": "Sicherheits-Updates offen",
  "platte-wird-knapp": "Platte wird knapp (unter 20 % frei)",
  "speicher-knapp": "Arbeitsspeicher knapp",
  "platte-unbekannt": "Platte unbekannt",
  "sicherung-unbekannt": "Sicherung unbekannt",
  "updates-unbekannt": "Updates unbekannt",
  "neustart-unbekannt": "Neustart-Stand unbekannt",
};
